import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Package, Truck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMarket } from "@/context/MarketContext";
import { useI18n } from "@/i18n/I18nContext";
import { formatPrice, FLAGS } from "@/lib/format";

export function ProductCard({ product, index = 0 }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const { favIds, refreshFavs } = useMarket();
  const s = product.summary || {};
  const isFav = favIds.includes(product.id);

  const toggleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error(t("common.loginPrompt"));
      return;
    }
    try {
      if (isFav) await api.delete(`/favorites/${product.id}`);
      else await api.post(`/favorites/${product.id}`);
      refreshFavs();
    } catch {
      toast.error("Something went wrong");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.4) }}
    >
      <Link
        to={`/product/${product.id}`}
        data-testid={`product-card-${product.id}`}
        className="group relative flex h-full flex-col rounded-lg border border-border bg-white p-5 transition-transform duration-300 hover:-translate-y-1 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        <button
          onClick={toggleFav}
          data-testid={`fav-btn-${product.id}`}
          aria-label="favorite"
          className={`absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full border transition-colors duration-200 ${
            isFav ? "border-rose-200 bg-rose-50 text-rose-500" : "border-border bg-white text-slate-400 hover:text-rose-500"
          }`}
        >
          <Heart className="h-4 w-4" fill={isFav ? "currentColor" : "none"} />
        </button>

        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary">{product.brand}</span>
        <h3 className="mt-1 font-heading text-lg font-semibold leading-tight tracking-tight text-slate-900">
          {product.part_name}
        </h3>
        <p className="mt-1 line-clamp-2 text-sm text-slate-500">{product.description}</p>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {(s.countries || []).map((c) => (
            <span key={c} className="text-sm" title={c}>{FLAGS[c]}</span>
          ))}
        </div>

        <div className="mt-4 flex items-end justify-between border-t border-border pt-4">
          <div>
            <div className="text-xs text-slate-400">{t("search.from")}</div>
            <div className="font-heading text-2xl font-bold tracking-tight text-slate-900">
              {formatPrice(s.min_total)}
            </div>
          </div>
          <div className="text-right text-xs text-slate-500">
            <div className="flex items-center justify-end gap-1"><Package className="h-3.5 w-3.5" />{s.offer_count} {t("search.offers")}</div>
            <div className="mt-1 flex items-center justify-end gap-1"><Truck className="h-3.5 w-3.5" />{s.best_delivery} {t("product.days")}</div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
